import React, { useState, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { throttle } from 'throttle-debounce';
import dynamic from 'next/dynamic';
import NoSSR from 'react-no-ssr';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { Overlay as ToggleOverlay } from 'planoplan-ui-kit/core/toggle';
import { IS_CLIENT, HEADER_ANIMATIONS } from '@constants';
import { ROUTES_NOT_FIXED_HEADER, ROUTES_NOT_FIXED_HEADER_BANNER } from '@constants/params';
import { ON_OPEN_ENTRY_MODAL } from '@constants/observer';
import { ScrollLock, useRenderOnlyDevice } from '@libs';
import { zIndex, Points } from '@styles';
import { useModal } from '@components/Modals';
import { HeaderBanner } from '@components/Banners';
import { auth } from '@modules/auth';
import { Navigation, Langs, MobileMenu, DownloadButton } from './organism';
import { Logo, Burger } from './molecules';
import { ui } from '../ui';

const EntryModal = dynamic(() => import('./modals').then((mod) => mod.EntryModal), { ssr: false });

const SCROLL_HEADER_COLOR = Colors.main_dark;
let timeout = '';
const screenHeight = IS_CLIENT ? window.outerHeight : 0;

export const Header = ({
  isDarkMode,
  isTransparent,
  isNoFixed = false,
  isHideOnScroll = false,
  wideHeader = false,
}) => {
  const isUser = useSelector((state) => !!state.user);
  const { locale } = useSelector((state) => state.settings);
  const router = useRouter();
  const { pathname, query } = router;
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isOpenEntryModal, onOpenEntryModal, onCloseEntryModal] = useModal();
  const [animation, setAnimation] = useState(HEADER_ANIMATIONS.NONE);
  const [isFixed, setIsFixed] = useState(false);
  const menuRef = useRef(null);
  const formEntryRedirectUrl = useRef(`/cabinet/`);
  const formEntryStep = useRef('login');
  const formEntryProps = useRef('props');
  const isMobileRender = useRenderOnlyDevice('mobile');
  const isNeedFixed = useRef(isMobileRender);
  const lastScrollY = useRef(0);

  const onChangeScroll = throttle(100, () => {
    const scrollY = window.scrollY;
    const isScrollUp = scrollY < lastScrollY.current;

    lastScrollY.current = scrollY;

    if (scrollY === 0 || !isNeedFixed.current) {
      setAnimation(HEADER_ANIMATIONS.NONE);
      setIsFixed(false);

      return;
    }

    if (isHideOnScroll) {
      if (scrollY < screenHeight) {
        if (isFixed) {
          setAnimation(HEADER_ANIMATIONS.HIDE);
          clearTimeout(timeout);

          timeout = setTimeout(() => {
            setIsFixed(false);
            setAnimation(HEADER_ANIMATIONS.NONE);
          }, 300);
        }

        return;
      }

      if (isScrollUp) {
        clearTimeout(timeout);
        setIsFixed(true);
        setAnimation(HEADER_ANIMATIONS.SHOW);
      } else if (isFixed) {
        setAnimation(HEADER_ANIMATIONS.HIDE);
      }

      return;
    }

    if (scrollY >= screenHeight && !isFixed && isNoFixed === false) {
      setAnimation(HEADER_ANIMATIONS.SHOW);
      setIsFixed(true);
      clearTimeout(timeout);
    }

    if (animation !== HEADER_ANIMATIONS.NONE && scrollY < screenHeight && isFixed && isNoFixed === false) {
      setAnimation(HEADER_ANIMATIONS.HIDE);

      timeout = setTimeout(() => {
        setIsFixed(false);
        setAnimation(HEADER_ANIMATIONS.NONE);
      }, 300);
    }
  });

  const onOpenMenu = () => {
    ScrollLock.lock(menuRef.current);
    setIsOpenMenu(true);
  };

  const onCloseMenu = () => {
    if (isOpenMenu) {
      ScrollLock.unlock(menuRef.current);
      setIsOpenMenu(false);
    }
  };

  const onBurger = () => (isOpenMenu ? onCloseMenu() : onOpenMenu());

  const onOpenStore = () => {
    onCloseMenu();
    // иначе возникает конфликт BodyScrollLock
    setTimeout(() => {
      ui.openStore();
    }, 100);
  };

  const onLogout = () => auth.logout({ locale });

  const onOpenModal = ({ step = 'login', redirectUrl = `/cabinet/`, props = {} }) => {
    formEntryRedirectUrl.current = redirectUrl;
    formEntryStep.current = step;
    formEntryProps.current = props;
    onOpenEntryModal();
    onCloseMenu();
  };

  const onOpenModalByHash = (hash) => {
    if (isUser) {
      return;
    }

    switch (hash) {
      case 'signup':
        onOpenModal({ step: 'signup' });
        break;
      case 'restore':
        onOpenModal({ step: 'recovery_password' });
        break;
    }
  };

  useEffect(() => {
    ui.addEventListener(ON_OPEN_ENTRY_MODAL, onOpenModal);

    const url = new URL(window.location.href);
    const { search } = url;
    if (!isUser && search === '?store=binding') {
      onOpenModal({ step: 'login' });
    }
    return () => ui.removeEventListener(ON_OPEN_ENTRY_MODAL);
  }, []);

  useEffect(() => {
    onCloseMenu();

    // на мобилке всегда фиксед
    if (!isMobileRender) {
      isNeedFixed.current = !ROUTES_NOT_FIXED_HEADER.includes(pathname);
    }
  }, [router, isMobileRender]);

  useEffect(() => {
    const onOrientationChange = () => onCloseMenu();

    window.addEventListener('orientationchange', onOrientationChange);
    window.addEventListener('resize', onOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', onOrientationChange);
      window.removeEventListener('resize', onOrientationChange);
    };
  }, [isOpenMenu]);

  useEffect(() => {
    document.addEventListener('scroll', onChangeScroll, { passive: true });

    return () => document.removeEventListener('scroll', onChangeScroll);
  }, [onChangeScroll]);

  const isBannerFixed = ROUTES_NOT_FIXED_HEADER_BANNER.includes(pathname);
  const isScrollHeader = isHideOnScroll && isFixed;

  return (
    <View>
      <Container
        isFixed={isFixed}
        isOpenMenu={isOpenMenu}
        animation={animation}
        isDarkMode={isDarkMode}
        isTransparent={isTransparent}
        isHideOnScroll={isHideOnScroll}>
        {isBannerFixed ? !isFixed && <HeaderBanner /> : <HeaderBanner />}
        <Wrapper
          isFixed={isFixed}
          isDarkMode={isDarkMode}
          isTransparent={isTransparent}
          isHideOnScroll={isHideOnScroll}
          wideHeader={wideHeader}>
          <Content wideHeader={wideHeader} isScrollUp={isScrollHeader}>
            <Logo isDarkMode={isDarkMode} isScrollUp={isScrollHeader} />
            <Navigation query={query} isDarkMode={isDarkMode} />
            <Langs isDarkMode={isDarkMode} isTransparent={isTransparent} isScrollUp={isScrollHeader} />
            <DownloadButton isDarkMode={isDarkMode} isTransparent={isTransparent} />
            <Burger isOpenMenu={isOpenMenu} onBurger={onBurger} isDarkMode={isDarkMode} locale={locale} />
          </Content>
        </Wrapper>
      </Container>

      <NoSSR>
        {isMobileRender && (
          <MobileMenu
            query={query}
            isOpenMenu={isOpenMenu}
            onCloseMenu={onCloseMenu}
            onOpenModal={onOpenModal}
            onOpenStore={onOpenStore}
            onLogout={onLogout}
            isUser={isUser}
            menuRef={menuRef}
            isFixed={isFixed}
            isDarkMode={isDarkMode}
            isTransparent={isTransparent}
          />
        )}

        <EntryModal
          isOpen={isOpenEntryModal}
          onClose={onCloseEntryModal}
          onOpenByHash={onOpenModalByHash}
          formEntryProps={formEntryProps.current}
          formEntryStep={formEntryStep.current}
          formEntryRedirectUrl={formEntryRedirectUrl.current}
        />
      </NoSSR>
    </View>
  );
};

const hide = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(-100%);
  }
`;

const show = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const View = styled.header`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;

  ${ToggleOverlay} {
    width: 100%;
  }
`;

const Container = styled.div`
  width: 100%;
  z-index: ${zIndex('main-header')};
  position: relative;
  background-color: ${(p) =>
    p.isDarkMode === true ? (p.isTransparent ? 'transparent' : `${Colors.main_black}`) : 'transparent'};
    
   ${(p) =>
     p.animation !== HEADER_ANIMATIONS.NONE &&
     css`
       animation-timing-function: linear;
       animation-fill-mode: forwards;
       animation-duration: 0.3s;
       animation-name: ${(p) =>
         (p.animation === HEADER_ANIMATIONS.SHOW && show) || (p.animation === HEADER_ANIMATIONS.HIDE && hide)};
     `}
  }
  
  ${(p) =>
    p.isFixed &&
    css`
      top: 0;
      position: sticky;
      background-color: ${(p) =>
        p.isHideOnScroll
          ? 'rgba(255, 255, 255, 0.4)'
          : p.isDarkMode === true
          ? p.isTransparent
            ? 'transparent'
            : `${Colors.main_black}`
          : `${Colors.white}`};
      border-bottom: ${(p) =>
        p.isHideOnScroll
          ? '1px solid rgb(234, 234, 234)'
          : p.isDarkMode === true
          ? `${Colors.main_black}`
          : `${Colors.whisper}`};
      backdrop-filter: ${(p) => p.isHideOnScroll && 'blur(20px)'};
      -webkit-backdrop-filter: ${(p) => p.isHideOnScroll && 'blur(20px)'};
    `}
  
  @media ${Points.tablet_portrait} {
    border-bottom: ${(p) => p.isOpenMenu && !p.isFixed && `1px solid ${Colors.whisper}`};
  
    ${(p) =>
      p.isOpenMenu &&
      css`
        ${Mixins.transition('background-color')};
        background-color: ${(p) => (p.isDarkMode ? Colors.main_black : Colors.white)};
      `}
  }
`;

const Wrapper = styled.div`
  margin: auto;
  max-width: ${(p) => (p.wideHeader ? '1600px' : '1440px')};
  padding-top: ${(p) => (p.isFixed ? '12px' : '15px')};
  padding-bottom: ${(p) => (p.isFixed ? '12px' : '15px')};
  background-color: ${(p) =>
    p.isHideOnScroll
      ? 'transparent'
      : p.isDarkMode === true
      ? p.isTransparent
        ? 'transparent'
        : `${Colors.main_black}`
      : 'transparent'};

  @media ${Points.tablet_landscape} {
    padding-top: ${(p) => (p.isFixed ? '15px' : '20px')};
    padding-bottom: ${(p) => (p.isFixed ? '15px' : '20px')};
  }

  @media ${Points.laptop} {
    padding-top: 20px;
    padding-bottom: 20px;
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 35px;
  padding: 0 16px;

  ${(p) =>
    p.isScrollUp &&
    css`
      &,
      & * {
        color: ${SCROLL_HEADER_COLOR} !important;
      }

      & > *:not([data-logo]) a,
      & > *:not([data-logo]) button {
        border-color: ${SCROLL_HEADER_COLOR} !important;
      }

      & > *:not([data-logo]) svg,
      & > *:not([data-logo]) svg * {
        stroke: ${SCROLL_HEADER_COLOR} !important;
      }

      & > button div::before,
      & > button div::after {
        background-color: ${SCROLL_HEADER_COLOR} !important;
      }
    `}

  @media ${Points.tablet_landscape} {
    justify-content: right;
    gap: 35px;
    width: ${(p) => p.wideHeader && '100%;'};
    max-width: 1024px;
    min-height: 44px;
    height: auto;
    margin: ${(p) => p.wideHeader && '0 auto'};
    padding: 0 28px;
  }

  @media ${Points.laptop} {
    margin: 0 auto
  }

  @media ${Points.laptop_wide} {
    width: auto;
    max-width: stretch;
    padding: 0 80px;
  }

  @media ${Points.fullhd} {
    width: ${(p) => (p.wideHeader ? '1600px' : '1386px')};
    max-width: ${(p) => (p.wideHeader ? '1600px' : '1386px')};
    padding: 0;
  }
`;
